/**
 * RestaurantCardFleetPanel — Card Fleet Ops, living inside the restaurant
 * Command Center.
 *
 * This is a MOVE, not a copy: it renders the real `CardFleetOps` surface in
 * `embedded` mode, so every KPI tile, filter, search, row selection, bulk
 * action, per-card menu, schedule editor and the Getting Started banner are
 * the same code the standalone page uses.
 *
 * Collapsible, OPEN by default, and the operator's choice is remembered per
 * user in this browser.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Boxes } from 'lucide-react';
import { CollapsibleToggle } from '../CollapsibleList';
import { useAuth } from '../../../lib/auth';
import CardFleetOps from '../../CardFleetOps';

const keyFor = (userId) => `yot.cc_fleet_open:${userId || 'anon'}`;

export default function RestaurantCardFleetPanel({ organizationId, scopedOrgId, locationIds }) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [open, setOpen] = useState(true);

    useEffect(() => {
        try {
            const saved = localStorage.getItem(keyFor(user?.id));
            if (saved !== null) setOpen(saved === '1');
        } catch { /* storage unavailable — stay open */ }
    }, [user?.id]);

    const toggle = () => {
        setOpen((o) => {
            const next = !o;
            try { localStorage.setItem(keyFor(user?.id), next ? '1' : '0'); } catch { /* ignore */ }
            return next;
        });
    };

    return (
        <section className="space-y-4" data-testid="cc-band-card-fleet">
            <CollapsibleToggle
                open={open}
                onToggle={toggle}
                testid="cc-band-card-fleet-toggle"
                size="lg"
                icon={<Boxes className="w-5 h-5 text-sky-500" strokeWidth={1.75} />}
                title={t('card_fleet.page_title')}
            />
            {open && (
                <div className="cc-fleet-embed rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm dark:border-slate-700/50 dark:bg-slate-900/40 dark:shadow-none"
                    data-testid="cc-card-fleet-body">
                    <CardFleetOps embedded
                        scopeOrgId={scopedOrgId !== undefined ? (scopedOrgId || '') : (organizationId || '')}
                        scopeLocationIds={locationIds || []} />
                </div>
            )}
        </section>
    );
}
